import {
  HHBox,
  HHCard,
  HHCardContent,
  HHChip,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import {
  ArrowForward,
  CheckCircle,
  Rule as RuleIcon,
  Warning,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { RuleSource } from '../hooks/use-sources';

type SourceCardProps = {
  source: RuleSource;
  onClick: (source: string) => void;
};

export const SourceCard = ({
  source,
  onClick,
}: SourceCardProps): JSX.Element => {
  const handleClick = (): void => onClick(source.source);

  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return 'Unknown';
    }
  };

  const isActive = source.status === 'active';
  const colorSet = isActive
    ? {
        primary: '#2E7D32',
        background: '#F1F8E9',
        border: '#4CAF50',
      }
    : {
        primary: '#757575',
        background: '#FAFAFA',
        border: '#BDBDBD',
      };

  const StatusIcon = isActive ? CheckCircle : Warning;

  return (
    <HHCard
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        height: '100%',
        minHeight: 180,
        border: `1px solid ${colorSet.border}`,
        borderRadius: '16px',
        position: 'relative',
        overflow: 'hidden',
        background: colorSet.background,
        '&:hover': {
          boxShadow: `0 12px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px ${colorSet.border}`,
          transform: 'translateY(-6px) scale(1.02)',
          '& .source-arrow': {
            transform: 'translateX(6px)',
            opacity: 1,
            color: colorSet.primary,
          },
        },
        '&:focus-visible': {
          outline: `3px solid ${colorSet.primary}`,
          outlineOffset: '2px',
        },
        transition: 'all 0.3s ease',
      }}
      tabIndex={0}
      role="button"
      onKeyDown={(e): void => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <HHCardContent sx={{ padding: '24px', height: '100%' }}>
        <HHStack direction="column" spacing={3} sx={{ height: '100%' }}>
          <HHStack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <HHStack direction="row" spacing={1.5} alignItems="center">
              <StatusIcon sx={{ fontSize: '22px', color: colorSet.primary }} />
              <RuleIcon sx={{ fontSize: '18px', color: colorSet.primary }} />
            </HHStack>
            <ArrowForward
              className="source-arrow"
              sx={{
                fontSize: '18px',
                color: 'text.secondary',
                opacity: 0.6,
                transform: 'translateX(0px)',
                transition: 'all 0.2s ease',
              }}
            />
          </HHStack>

          <HHStack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <HHTypography
              hhVariant="h6"
              noWrap
              title={source.source}
              sx={{ fontWeight: 600 }}
            >
              {source.source}
            </HHTypography>
            <HHChip
              label={isActive ? 'Active' : 'Inactive'}
              color={isActive ? 'success' : 'default'}
              size="small"
              hhVariant="filled"
            />
          </HHStack>

          <HHBox>
            <HHTypography hhVariant="body2" sx={{ color: 'text.secondary' }}>
              {source.rulesCount} rule{source.rulesCount !== 1 ? 's' : ''}
            </HHTypography>
            <HHTypography
              hhVariant="body2"
              sx={{ color: 'text.secondary', marginTop: '2px' }}
            >
              Updated: {formatDate(source.lastUpdated)}
            </HHTypography>
          </HHBox>
        </HHStack>
      </HHCardContent>
    </HHCard>
  );
};
